import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useLazyQuery } from "@apollo/client";
import { SearchQuery } from "@graphqlTypes";
import { Search } from "@/queries/Search";
import { useRouter } from "next/router";
import { getUrlLocale } from "@/core/utils";
import { useTracking } from "./TrackingContext";

type SearchContextType = {
  loading: boolean;
  autoCompleteLoading: boolean;
  hasSearched: boolean;
  searchTerm: string;
  lastSearchTerm: string;
  showSearchTray: boolean;
  termCounts: TermCount[];
  resultsCount: number;
  isSearchPage: boolean;
  ignoreSearch: boolean;
  language: string | null;
  searchPath: string;
  perPage: number;
  currentPage: number;
  goingToSearch: boolean;
  results: SearchQuery["search"]["nodes"];
  autoCompleteResults: SearchQuery["search"]["nodes"];
  shopResults: ShopifyCarouselProduct[];
  resetSearch: () => void;
  updateResults: () => void;
  setIgnoreSearch: (value: boolean) => void;
  setIsSearchPage: (value: boolean) => void;
  setExclude: (value: number[]) => void;
  setCurrentPage: (page: number) => void;
  setHasSearched: (value: boolean) => void;
  setSearchTerm: (value: string) => void;
  setShowSearchTray: (value: boolean) => void;
  setGoingToSearch: (value: boolean) => void;
  setResults: (results: SearchQuery["search"]["nodes"]) => void;
  setShopResults: (results: ShopifyCarouselProduct[]) => void;
  fetchAutoCompleteResults: (value: string) => void;
  fetchSearchResults: (value: string, filters?: number[]) => void;
};

type SearchProviderProps = {
  children: ReactNode;
};

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState("");
  const [lastSearchTerm, setLastSearchTerm] = useState("");
  const currentSearchTerm = useRef("");
  const goingToSearch = useRef(false);
  const [resultsCount, setResultsCount] = useState(0);
  const [nextResultsCount, setNextResultsCount] = useState(0);
  const [showSearchTray, setShowSearchTray] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [termCounts, setTermCounts] = useState<TermCount[]>([]);
  const [ignoreSearch, setIgnoreSearch] = useState(false);
  const currentPageRef = useRef(1);
  const [currentPage, _setCurrentPage] = useState(1);
  const isSearchPageRef = useRef(false);
  const [isSearchPage, setIsSearchPage] = useState(false);
  const perPage = 8;
  const autoCompletePerPage = 3;
  const exclude = useRef<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoCompleteLoading, setAutoCompleteLoading] = useState(false);
  const [results, setResults] = useState<SearchQuery["search"]["nodes"]>([]);
  const [nextResults, setNextResults] = useState<SearchQuery["search"]["nodes"]>([]);
  const language = useMemo(() => getUrlLocale(router.asPath), [router.asPath]);
  const searchPath = useMemo(() => language && language !== 'en' ? `/${language}/search` : '/search', [language]);
  const [autoCompleteResults, setAutocompleteResults] = useState<
    SearchQuery["search"]["nodes"]
  >([]);
  const [shopResults, setShopResults] = useState<ShopifyCarouselProduct[]>([]);
  const { trackSearch } = useTracking();

  // Search runs over the theme's REST endpoint (via the same-origin /api/search
  // proxy) instead of GraphQL: every GraphQL request pays ~2s of ACF schema
  // registration that search doesn't need. The response is shaped exactly like
  // the Apollo Search query result (queries/Search.ts remains the type source;
  // parity is enforced by qa/search-perf/rest-parity.js), so consumers are
  // unchanged. The server never implemented the old exclude/offset variables,
  // so they are not part of this signature.
  //
  // In-memory cache mirrors Apollo's old client cache: identical requests are
  // served without a network call for the lifetime of the page. Capped so long
  // sessions don't grow unbounded (insertion-order eviction). Payloads whose
  // Shopify half failed server-side (shopifyFailed) render but are never
  // cached, so shop results reappear as soon as Shopify recovers. In-flight
  // dedup mirrors Apollo's queryDeduplication: concurrent identical requests
  // share one fetch.
  const searchCache = useRef(new Map<string, SearchQuery>());
  const searchInFlight = useRef(new Map<string, Promise<{ data: SearchQuery | undefined }>>());
  const SEARCH_CACHE_MAX = 50;

  // Dev escape hatch: add ?searchViaGraphql=1 to any page URL and searches on
  // that page run through the old GraphQL path (with Apollo's own caching),
  // for comparing results and timing against the REST endpoint
  const [getGraphqlSearchResults] = useLazyQuery<SearchQuery>(Search);
  const searchViaGraphql = () =>
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("searchViaGraphql");

  const getSearchResults = async (term: string, filters: number[] = []): Promise<{ data: SearchQuery | undefined }> => {
    if (searchViaGraphql()) {
      console.info("[search] serving via GraphQL (searchViaGraphql param active)");
      return getGraphqlSearchResults({
        variables: { searchTerm: term, language, pageCategoryFilters: filters, offset: 0 },
      });
    }
    const params = new URLSearchParams({ s: term });
    if (language) params.set("language", language);
    if (filters.length) params.set("filters", filters.join(","));
    const cacheKey = params.toString();
    const cached = searchCache.current.get(cacheKey);
    if (cached) return { data: cached };
    const inFlight = searchInFlight.current.get(cacheKey);
    if (inFlight) return inFlight;
    const request = (async () => {
      const res = await fetch(`/api/search/?${cacheKey}`);
      if (!res.ok) throw new Error(`Search request failed: ${res.status}`);
      const payload = (await res.json()) as SearchQuery & { shopifyFailed?: boolean };
      if (payload?.search && !payload.shopifyFailed) {
        if (searchCache.current.size >= SEARCH_CACHE_MAX) {
          searchCache.current.delete(searchCache.current.keys().next().value as string);
        }
        searchCache.current.set(cacheKey, payload);
      }
      return { data: payload as SearchQuery };
    })();
    searchInFlight.current.set(cacheKey, request);
    try {
      return await request;
    } finally {
      searchInFlight.current.delete(cacheKey);
    }
  };

  const setExclude = (value: number[]) => {
    exclude.current = value;
  };

  const setCurrentPage = (page: number) => {
    _setCurrentPage(page);
    currentPageRef.current = page;
  };

  const setGoingToSearch = (value: boolean) => {
    goingToSearch.current = value;
  }

  const resetSearch = () => {
    setSearchTerm("");
    setHasSearched(false);
    setAutoCompleteLoading(false);
    setLastSearchTerm("");
    currentSearchTerm.current = "";
    currentPageRef.current = 1;
    setTimeout(() => {
      setAutocompleteResults([]);
      setResults([]);
      setShopResults([]);
    }, 350);
  }

  const updateResults = () => {
    if (nextResults.length) {
      setCurrentPage(1);
      setResults(nextResults);
      setResultsCount(nextResultsCount);
      setHasSearched(true);
    }
  }

  // The tray input debounces keystrokes before calling this (see SearchTray),
  // so the fetch fires immediately and results render as soon as they arrive.
  // The previous fixed 400ms pre-fetch hold and 300ms minimum spinner stacked
  // on top of that debounce and added ~700ms to every search, including
  // client-cache hits that resolve instantly.
  const fetchAutoCompleteResults = async (term: string) => {
    if (term) {
      trackSearch(term, true);
      currentSearchTerm.current = term;
      setAutoCompleteLoading(true);
      getSearchResults(term)
        .then(({ data }) => {
          if (currentSearchTerm.current !== term) return;
          if (data?.search?.nodes.length) {
            setAutocompleteResults(data.search.nodes.slice(0, autoCompletePerPage));
            if (!isSearchPageRef.current) {
              setResults(data.search.nodes);
              setResultsCount(data.search.pageInfo?.total);
              setTermCounts(data.search.termCounts || []);
            } else {
              setNextResults(data.search.nodes);
              setTermCounts(data.search.termCounts || []);
              setNextResultsCount(data.search.pageInfo?.total);
            }
          } else {
            setAutocompleteResults([]);
          }
          setShopResults((data?.search?.shopifyProducts ?? []) as ShopifyCarouselProduct[]);
          setLastSearchTerm(term);
          setHasSearched(true);
          setAutoCompleteLoading(false);
      })
      .catch((err) => {
        if (currentSearchTerm.current !== term) return;
        setAutoCompleteLoading(false);
      });
    } else {
      setAutocompleteResults([]);
    }
  };

  // Guards against out-of-order resolution: with instant cache hits, a slow
  // older request can resolve after a newer one has already rendered
  const currentPageSearchKey = useRef("");

  const fetchSearchResults = async (term: string, filters?: number[]) => {
    if (term) {
      trackSearch(term, false);
      setLoading(true);
      const requestKey = `${term}|${(filters || []).join(",")}`;
      currentPageSearchKey.current = requestKey;
      getSearchResults(term, filters || [])
        .then(({ data }) => {
          if (currentPageSearchKey.current !== requestKey) return;
          if (data?.search?.nodes) {
            setResults(data.search.nodes);
            setResultsCount(data.search.pageInfo?.total);
            setTermCounts(data.search.termCounts || []);
            setShopResults((data.search.shopifyProducts ?? []) as ShopifyCarouselProduct[]);
          } else {
            setResults([]);
            setResultsCount(0);
            setShopResults([]);
          }
          setSearchTerm(term);
          setLastSearchTerm(term);
          setLoading(false);
          setHasSearched(true);
        })
        .catch((err) => {
          if (currentPageSearchKey.current !== requestKey) return;
          console.error("Error fetching search results:", err);
          setLoading(false);
          setHasSearched(true);
        });
    } else {
      setResults([]);
      setResultsCount(0);
      setLoading(false);
    }
  };

  useEffect(() => { isSearchPageRef.current = isSearchPage; }, [isSearchPage]);

  return (
    <SearchContext.Provider
      value={{
        // State
        loading,
        searchTerm,
        lastSearchTerm,
        hasSearched,
        perPage,
        termCounts,
        results,
        searchPath,
        language,
        ignoreSearch,
        isSearchPage,
        resultsCount,
        autoCompleteLoading,
        goingToSearch: goingToSearch.current,
        currentPage,
        autoCompleteResults,
        shopResults,

        // Actions
        setSearchTerm,
        setGoingToSearch,
        setIgnoreSearch,
        resetSearch,
        setHasSearched,
        updateResults,
        setExclude,
        setResults,
        setShopResults,
        setIsSearchPage,
        fetchAutoCompleteResults,
        setCurrentPage,
        fetchSearchResults,
        setShowSearchTray,
        showSearchTray,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  return context || {
    loading: false,
    autoCompleteLoading: false,
    hasSearched: false,
    searchTerm: '',
    language: 'en',
    searchPath: '/search',
    termCounts: [],
    lastSearchTerm: '',
    showSearchTray: false,
    resultsCount: 0,
    isSearchPage: false,
    ignoreSearch: false,
    perPage: 6,
    currentPage: 1,
    goingToSearch: false,
    results: [],
    autoCompleteResults: [],
    shopResults: [],
    resetSearch: () => {},
    updateResults: () => {},
    setIgnoreSearch: () => {},
    setIsSearchPage: () => {},
    setExclude: () => {},
    setCurrentPage: () => {},
    setHasSearched: () => {},
    setSearchTerm: () => {},
    setShowSearchTray: () => {},
    setGoingToSearch: () => {},
    setResults: () => {},
    setShopResults: () => {},
    fetchAutoCompleteResults: () => {},
    fetchSearchResults: () => {},
  }
};
